import type { BusinessHour } from "@/types/directory";

/** Convert a 24-hour "HH:MM" string into a friendly "7:30 PM" label. */
export function formatTime(value: string): string {
  if (!value || typeof value !== "string") return "";
  const [hourPart, minutePart = "00"] = value.split(":");
  let hour = parseInt(hourPart, 10);
  if (Number.isNaN(hour)) return value;
  const suffix = hour >= 12 ? "PM" : "AM";
  hour %= 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minutePart} ${suffix}`;
}

function isAllDay(entry: BusinessHour): boolean {
  if (!entry || !entry.open) return false;
  return entry.open === "00:00" && (entry.close === "23:59" || entry.close === "00:00");
}

/** Convert a 24-hour "HH:MM" string into minutes since midnight. */
function toMinutes(value: string): number {
  if (!value || typeof value !== "string") return NaN;
  const [hourPart, minutePart = "0"] = value.split(":");
  const hour = parseInt(hourPart, 10);
  const minute = parseInt(minutePart, 10);
  if (Number.isNaN(hour)) return NaN;
  return hour * 60 + (Number.isNaN(minute) ? 0 : minute);
}

/**
 * Compute whether a listing is open right now from its weekly hours and the
 * current clock. Handles all-day listings and overnight windows that roll past
 * midnight. Falls back to the provided flag when no hours are available.
 */
export function computeOpenNow(hours: BusinessHour[], fallback = false): boolean {
  if (!hours || hours.length === 0) return fallback;

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const todayIndex = (now.getDay() + 6) % 7;

  const today = hours[todayIndex];
  if (today && !today.closed) {
    if (isAllDay(today)) return true;
    const open = toMinutes(today.open);
    const close = toMinutes(today.close);
    if (!Number.isNaN(open) && !Number.isNaN(close)) {
      if (nowMinutes >= open && nowMinutes < close) return true;
    }
  }

  // Yesterday's shift may still be running if it crosses midnight (e.g. 22:00-02:00).
  const prevIndex = (todayIndex + 6) % 7;
  const prev = hours[prevIndex];
  if (prev && !prev.closed) {
    const open = toMinutes(prev.open);
    const close = toMinutes(prev.close);
    if (!Number.isNaN(open) && !Number.isNaN(close) && close <= open && nowMinutes < close) {
      return true;
    }
  }

  return false;
}

/**
 * Build a short status hint that complements the "Open now" indicator,
 * e.g. "Closes 10:30 PM" when open, or "Opens 9:30 AM Mon" when closed.
 */
export function getHoursHint(hours: BusinessHour[], openNow: boolean): string {
  if (!hours || hours.length === 0) return "";
  const todayIndex = (new Date().getDay() + 6) % 7;

  if (openNow) {
    const today = hours[todayIndex];
    if (today && !today.closed) {
      return isAllDay(today) ? "Open 24 hours" : `Closes ${formatTime(today.close)}`;
    }
  }

  for (let offset = 0; offset < 7; offset += 1) {
    const entry = hours[(todayIndex + offset) % 7];
    if (!entry || entry.closed) continue;
    if (isAllDay(entry)) return "Open 24 hours";
    if (offset === 0) return `Opens ${formatTime(entry.open)}`;
    return `Opens ${(entry.day ?? "").slice(0, 3)} ${formatTime(entry.open)}`;
  }

  return "";
}